import { formatDate } from 'date-fns';
import { CompanyFilterCriteriaProperty, FilterOperator, ICompanyFilter } from '../../viewModels/companyfilter';

/** Request body for POST /lead/demos/filter and POST /lead/demosToConfirm/filter */
export interface DemoFilterRequest {
	filter?: ICompanyFilter;
	userId?: string;
	managerUserId?: string;
}

/** Parameters the UI collects that get converted into a DemoFilterRequest */
export interface DemoFilterParams {
	// Top-level fields (not in criteria)
	userId?: string | null;
	managerUserId?: string | null;

	// Single-value criteria
	companyId?: string | null;
	outcomeName?: string | null;
	isColdDemo?: boolean | null;
	overbook?: boolean | null;

	// Multi-value criteria (OR-grouped)
	dealStage?: string[];
	leadSource?: string[] | null;
	industry?: string[] | null;
	tag?: string[] | null;
	addOn?: string[] | null;
	inboundChannel?: string[] | null;
	lostReasonCategories?: string[] | null;
	demoPerformerUserId?: string[] | null;

	// Date ranges
	start?: Date | null;
	end?: Date | null;
	followUpDateStart?: Date | null;
	followUpDateEnd?: Date | null;
	targetCloseDateStart?: Date | null;
	targetCloseDateEnd?: Date | null;
}

/** Build a single leaf criterion */
function leaf(property: CompanyFilterCriteriaProperty, value: string): ICompanyFilter {
	return { property, value };
}

/** Build a date criterion with Gt or Lt operator */
function dateCriterion(
	property: CompanyFilterCriteriaProperty,
	op: FilterOperator.Gt | FilterOperator.Lt,
	date: Date
): ICompanyFilter {
	return { property, op, value: formatDate(date, "yyyy-MM-dd'T'00:00:00'Z'") };
}

/** Build an OR group from multiple values for the same property. Single-item arrays produce a leaf (no wrapper). */
function orGroup(property: CompanyFilterCriteriaProperty, values: string[]): ICompanyFilter {
	if (values.length === 1) {
		return leaf(property, values[0]);
	}
	return {
		op: FilterOperator.Or,
		criteria: values.map(v => leaf(property, v)),
	};
}

/**
 * Converts UI filter params into the nested CompanyFilter POST body.
 * All non-empty criteria are AND'd at the top level.
 */
export function buildDemoFilterBody(params: DemoFilterParams): DemoFilterRequest {
	const criteria: ICompanyFilter[] = [];

	// Single-value criteria
	if (params.companyId) {
		criteria.push(leaf(CompanyFilterCriteriaProperty.Id, params.companyId));
	}
	if (params.outcomeName) {
		criteria.push(leaf(CompanyFilterCriteriaProperty.LastInteractionType, params.outcomeName));
	}
	if (params.isColdDemo != null) {
		criteria.push(leaf(CompanyFilterCriteriaProperty.IsColdDemo, params.isColdDemo ? 'True' : 'False'));
	}
	if (params.overbook != null) {
		criteria.push(leaf(CompanyFilterCriteriaProperty.DealOverbook, params.overbook ? 'True' : 'False'));
	}

	// Multi-value criteria (OR-grouped)
	if (params.dealStage && params.dealStage.length > 0) {
		criteria.push(orGroup(CompanyFilterCriteriaProperty.LastDealStage, params.dealStage));
	}
	if (params.leadSource && params.leadSource.length > 0) {
		criteria.push(orGroup(CompanyFilterCriteriaProperty.DealLeadSource, params.leadSource));
	}
	if (params.industry && params.industry.length > 0) {
		criteria.push(orGroup(CompanyFilterCriteriaProperty.LeadIndustry, params.industry));
	}
	if (params.tag && params.tag.length > 0) {
		criteria.push(orGroup(CompanyFilterCriteriaProperty.Tag, params.tag));
	}
	if (params.addOn && params.addOn.length > 0) {
		criteria.push(orGroup(CompanyFilterCriteriaProperty.DealAddOn, params.addOn));
	}
	if (params.inboundChannel && params.inboundChannel.length > 0) {
		criteria.push(orGroup(CompanyFilterCriteriaProperty.DealInboundChannel, params.inboundChannel));
	}
	if (params.lostReasonCategories && params.lostReasonCategories.length > 0) {
		criteria.push(orGroup(CompanyFilterCriteriaProperty.DealLostReasonCategories, params.lostReasonCategories));
	}
	if (params.demoPerformerUserId && params.demoPerformerUserId.length > 0) {
		criteria.push(orGroup(CompanyFilterCriteriaProperty.DealDemoPerformer, params.demoPerformerUserId));
	}

	// Date ranges
	if (params.start) {
		criteria.push(dateCriterion(CompanyFilterCriteriaProperty.NextMeetingDate, FilterOperator.Gt, params.start));
	}
	if (params.end) {
		criteria.push(dateCriterion(CompanyFilterCriteriaProperty.NextMeetingDate, FilterOperator.Lt, params.end));
	}
	if (params.followUpDateStart) {
		criteria.push(
			dateCriterion(CompanyFilterCriteriaProperty.DealFollowUpDate, FilterOperator.Gt, params.followUpDateStart)
		);
	}
	if (params.followUpDateEnd) {
		criteria.push(
			dateCriterion(CompanyFilterCriteriaProperty.DealFollowUpDate, FilterOperator.Lt, params.followUpDateEnd)
		);
	}
	if (params.targetCloseDateStart) {
		criteria.push(
			dateCriterion(CompanyFilterCriteriaProperty.DealTargetCloseDate, FilterOperator.Gt, params.targetCloseDateStart)
		);
	}
	if (params.targetCloseDateEnd) {
		criteria.push(
			dateCriterion(CompanyFilterCriteriaProperty.DealTargetCloseDate, FilterOperator.Lt, params.targetCloseDateEnd)
		);
	}
	return {
		filter: criteria.length > 0 ? { criteria } : undefined,
		userId: params.userId ?? undefined,
		managerUserId: params.managerUserId ?? undefined,
	};
}
